import { useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { useTranslation } from 'react-i18next';
import {
  LinkIcon,
  ClipboardDocumentIcon,
  CheckIcon,
  LockClosedIcon,
  CalendarIcon,
  ArrowDownTrayIcon,
  CloudArrowUpIcon,
  XMarkIcon,
  DocumentIcon,
} from '@heroicons/react/24/outline';
import { useUIStore } from '../../stores/uiStore';
import { useFileStore } from '../../stores/fileStore';
import { publicLinksApi } from '../../api/public-links';
import { Modal } from '../ui/Modal';
import { Input } from '../ui/Input';
import { Button } from '../ui/Button';
import { useToast } from '../ui/Toaster';
import clsx from 'clsx';

type LinkType = 'download' | 'upload';
type SizeUnit = 'MB' | 'GB';

export function CreateLinkModal() {
  const { t } = useTranslation('files');
  const [linkType, setLinkType] = useState<LinkType>('download');
  const [password, setPassword] = useState('');
  const [usePassword, setUsePassword] = useState(false);
  const [maxDownloads, setMaxDownloads] = useState('');
  const [maxUploads, setMaxUploads] = useState('');
  const [expiresIn, setExpiresIn] = useState('7'); // days
  const [isLoading, setIsLoading] = useState(false);
  const [generatedLink, setGeneratedLink] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  // Upload limits
  const [maxFileSize, setMaxFileSize] = useState('');
  const [maxFileSizeUnit, setMaxFileSizeUnit] = useState<SizeUnit>('MB');
  const [extensionInput, setExtensionInput] = useState('');
  const [allowedExtensions, setAllowedExtensions] = useState<string[]>([]);

  const isOpen = useUIStore((s) => s.isCreateLinkModalOpen);
  const closeModal = useUIStore((s) => s.closeCreateLinkModal);
  const contextMenuTarget = useUIStore((s) => s.contextMenuTarget);

  const files = useFileStore((s) => s.files);
  const folders = useFileStore((s) => s.folders);
  const toast = useToast();

  const currentItem =
    contextMenuTarget?.type === 'file'
      ? files.find((f) => f.id === contextMenuTarget.id)
      : folders.find((f) => f.id === contextMenuTarget?.id);

  const addExtension = () => {
    const ext = extensionInput.trim().toLowerCase();
    if (!ext) return;
    // Normalize extension (add dot if missing)
    const normalizedExt = ext.startsWith('.') ? ext : `.${ext}`;
    if (!allowedExtensions.includes(normalizedExt)) {
      setAllowedExtensions([...allowedExtensions, normalizedExt]);
    }
    setExtensionInput('');
  };

  const removeExtension = (ext: string) => {
    setAllowedExtensions(allowedExtensions.filter((e) => e !== ext));
  };

  const handleExtensionKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      addExtension();
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!contextMenuTarget) return;

    setIsLoading(true);
    try {
      const expiresAt = expiresIn
        ? new Date(Date.now() + parseInt(expiresIn) * 24 * 60 * 60 * 1000).toISOString()
        : undefined;

      // Calculate max file size in bytes
      let maxFileSizeBytes: number | undefined;
      if (linkType === 'upload' && maxFileSize) {
        const size = parseFloat(maxFileSize);
        maxFileSizeBytes = maxFileSizeUnit === 'GB' ? size * 1024 * 1024 * 1024 : size * 1024 * 1024;
      }

      const { link } = await publicLinksApi.create({
        fileId: contextMenuTarget.type === 'file' ? contextMenuTarget.id : undefined,
        folderId: contextMenuTarget.type === 'folder' ? contextMenuTarget.id : undefined,
        linkType,
        password: usePassword && password ? password : undefined,
        maxDownloads: linkType === 'download' && maxDownloads ? parseInt(maxDownloads) : undefined,
        maxUploads: linkType === 'upload' && maxUploads ? parseInt(maxUploads) : undefined,
        maxFileSize: maxFileSizeBytes,
        allowedExtensions: linkType === 'upload' && allowedExtensions.length > 0 ? allowedExtensions : undefined,
        expiresAt,
      });

      const fullUrl = `${window.location.origin}/${linkType === 'download' ? 'share' : 'upload'}/${link.token}`;
      setGeneratedLink(fullUrl);
      toast.success(t('shareLink.created'), t('shareLink.shareToGrant'));
    } catch {
      toast.error(t('toast.createLinkFailed'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleCopy = async () => {
    if (!generatedLink) return;
    try {
      await navigator.clipboard.writeText(generatedLink);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      toast.success(t('toast.copiedToClipboard'));
    } catch {
      toast.error(t('toast.copyFailed'));
    }
  };

  const handleClose = () => {
    setLinkType('download');
    setPassword('');
    setUsePassword(false);
    setMaxDownloads('');
    setMaxUploads('');
    setExpiresIn('7');
    setGeneratedLink(null);
    setCopied(false);
    setMaxFileSize('');
    setMaxFileSizeUnit('MB');
    setExtensionInput('');
    setAllowedExtensions([]);
    closeModal();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={t('shareLink.title')}
      size="md"
    >
      <AnimatePresence mode="wait">
        {generatedLink ? (
          <motion.div
            key="success"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="space-y-4"
          >
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
                <LinkIcon className="w-8 h-8 text-green-600 dark:text-green-400" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                {t('shareLink.created')}
              </h3>
              <p className="text-gray-500 dark:text-slate-400 text-sm mt-1">
                {t('shareLink.shareAccess', { type: linkType === 'download' ? t('shareLink.download') : t('shareLink.upload') })}
              </p>
            </div>

            <div className="flex gap-2">
              <Input
                value={generatedLink}
                readOnly
                className="flex-1 font-mono text-sm"
              />
              <Button onClick={handleCopy} variant="secondary">
                {copied ? (
                  <CheckIcon className="w-5 h-5 text-green-500" />
                ) : (
                  <ClipboardDocumentIcon className="w-5 h-5" />
                )}
              </Button>
            </div>

            <Button onClick={handleClose} className="w-full">
              {t('common:buttons.done')}
            </Button>
          </motion.div>
        ) : (
          <motion.form
            key="form"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            onSubmit={handleSubmit}
            className="space-y-5"
          >
            {/* Item info */}
            <div className="bg-gray-50 dark:bg-slate-800 rounded-lg p-3 flex items-center gap-3">
              <div className="w-10 h-10 bg-primary-100 dark:bg-primary-900/30 rounded-lg flex items-center justify-center">
                {contextMenuTarget?.type === 'folder' ? (
                  <CloudArrowUpIcon className="w-5 h-5 text-primary-600 dark:text-primary-400" />
                ) : (
                  <ArrowDownTrayIcon className="w-5 h-5 text-primary-600 dark:text-primary-400" />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-medium text-gray-900 dark:text-white truncate">
                  {currentItem?.name}
                </p>
                <p className="text-sm text-gray-500 dark:text-slate-400">
                  {contextMenuTarget?.type === 'folder' ? t('shareLink.folder') : t('shareLink.file')}
                </p>
              </div>
            </div>

            {/* Link type selector */}
            {contextMenuTarget?.type === 'folder' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-2">
                  {t('shareLink.linkType')}
                </label>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    onClick={() => setLinkType('download')}
                    className={clsx(
                      'p-3 rounded-lg border-2 text-left transition-all',
                      linkType === 'download'
                        ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                        : 'border-gray-200 dark:border-slate-600 hover:border-gray-300 dark:hover:border-slate-500'
                    )}
                  >
                    <ArrowDownTrayIcon className="w-5 h-5 text-primary-600 dark:text-primary-400 mb-1" />
                    <p className="font-medium text-gray-900 dark:text-white text-sm">{t('shareLink.download')}</p>
                    <p className="text-xs text-gray-500 dark:text-slate-400">{t('shareLink.allowDownload')}</p>
                  </button>
                  <button
                    type="button"
                    onClick={() => setLinkType('upload')}
                    className={clsx(
                      'p-3 rounded-lg border-2 text-left transition-all',
                      linkType === 'upload'
                        ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                        : 'border-gray-200 dark:border-slate-600 hover:border-gray-300 dark:hover:border-slate-500'
                    )}
                  >
                    <CloudArrowUpIcon className="w-5 h-5 text-primary-600 dark:text-primary-400 mb-1" />
                    <p className="font-medium text-gray-900 dark:text-white text-sm">{t('shareLink.upload')}</p>
                    <p className="text-xs text-gray-500 dark:text-slate-400">{t('shareLink.allowUpload')}</p>
                  </button>
                </div>
              </div>
            )}

            {/* Password protection */}
            <div>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={usePassword}
                  onChange={(e) => setUsePassword(e.target.checked)}
                  className="w-4 h-4 rounded border-gray-300 dark:border-slate-600 text-primary-600 focus:ring-primary-500"
                />
                <LockClosedIcon className="w-4 h-4 text-gray-500 dark:text-slate-400" />
                <span className="text-sm text-gray-700 dark:text-slate-300">{t('shareLink.passwordProtect')}</span>
              </label>
              {usePassword && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  className="mt-2"
                >
                  <Input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder={t('shareLink.enterPassword')}
                  />
                </motion.div>
              )}
            </div>

            {/* Expiration */}
            <div>
              <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-slate-300 mb-2">
                <CalendarIcon className="w-4 h-4" />
                {t('shareLink.expiresIn')}
              </label>
              <select
                value={expiresIn}
                onChange={(e) => setExpiresIn(e.target.value)}
                className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              >
                <option value="1">{t('shareLink.days', { count: 1 })}</option>
                <option value="7">{t('shareLink.days', { count: 7 })}</option>
                <option value="30">{t('shareLink.days', { count: 30 })}</option>
                <option value="90">{t('shareLink.days', { count: 90 })}</option>
                <option value="">{t('shareLink.never')}</option>
              </select>
            </div>

            {/* Download/Upload limits */}
            {linkType === 'download' ? (
              <Input
                type="number"
                label={t('shareLink.maxDownloads')}
                value={maxDownloads}
                onChange={(e) => setMaxDownloads(e.target.value)}
                placeholder={t('shareLink.unlimited')}
                min="1"
              />
            ) : (
              <>
                <Input
                  type="number"
                  label={t('shareLink.maxUploads')}
                  value={maxUploads}
                  onChange={(e) => setMaxUploads(e.target.value)}
                  placeholder={t('shareLink.unlimited')}
                  min="1"
                />

                {/* Max file size - only for upload links */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-2">
                    {t('shareLink.maxFileSize')}
                  </label>
                  <div className="flex gap-2">
                    <Input
                      type="number"
                      value={maxFileSize}
                      onChange={(e) => setMaxFileSize(e.target.value)}
                      placeholder={t('shareLink.noLimit')}
                      min="1"
                      className="flex-1"
                    />
                    <select
                      value={maxFileSizeUnit}
                      onChange={(e) => setMaxFileSizeUnit(e.target.value as SizeUnit)}
                      className="px-3 py-2 rounded-lg border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    >
                      <option value="MB">MB</option>
                      <option value="GB">GB</option>
                    </select>
                  </div>
                </div>

                {/* Allowed extensions - only for upload links */}
                <div>
                  <label className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-slate-300 mb-2">
                    <DocumentIcon className="w-4 h-4" />
                    {t('shareLink.allowedTypes')}
                  </label>
                  <div className="flex gap-2 mb-2">
                    <Input
                      value={extensionInput}
                      onChange={(e) => setExtensionInput(e.target.value)}
                      onKeyDown={handleExtensionKeyDown}
                      placeholder={t('shareLink.extensionPlaceholder')}
                      className="flex-1"
                    />
                    <Button type="button" variant="secondary" onClick={addExtension}>
                      {t('common:buttons.add')}
                    </Button>
                  </div>
                  {allowedExtensions.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      {allowedExtensions.map((ext) => (
                        <span
                          key={ext}
                          className="inline-flex items-center gap-1 px-2 py-1 bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 rounded-lg text-sm"
                        >
                          {ext}
                          <button
                            type="button"
                            onClick={() => removeExtension(ext)}
                            className="p-0.5 hover:bg-primary-200 dark:hover:bg-primary-800 rounded"
                          >
                            <XMarkIcon className="w-3 h-3" />
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                  {allowedExtensions.length === 0 && (
                    <p className="text-xs text-gray-500 dark:text-slate-400">
                      {t('shareLink.allowAllTypes')}
                    </p>
                  )}
                </div>
              </>
            )}

            <div className="flex gap-3 pt-2">
              <Button
                type="button"
                variant="secondary"
                onClick={handleClose}
                className="flex-1"
              >
                {t('common:buttons.cancel')}
              </Button>
              <Button
                type="submit"
                isLoading={isLoading}
                className="flex-1"
              >
                <LinkIcon className="w-4 h-4" />
                {t('shareLink.createLink')}
              </Button>
            </div>
          </motion.form>
        )}
      </AnimatePresence>
    </Modal>
  );
}
